import React, { useState, useCallback, useRef } from 'react';
import { Box, Container, Grid, Typography, Button, Card, CardContent, CardHeader } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ICompanyDetails } from '../../redux/company/types';
import { getJWtDetails } from '../../utils/AppUtils';
import { Edit, Save, X } from 'lucide-react';
import {
    CompanyBasicInfoEdit,
    CompanyAboutEdit,
    CompanyServicesEdit,
    CompanyLeadershipEdit,
    CompanyContactEdit,
    CompanyVideosEdit,
    CompanyGalleryEdit,
} from './EditSections';

interface ICompanyEditContainer {
    companyDetails?: ICompanyDetails;
    onCancel?: () => void;
}

type EditSection = 'basic' | 'about' | 'services' | 'leadership' | 'contact' | 'videos' | 'gallery';

const CompanyEditContainer: React.FC<ICompanyEditContainer> = ({ companyDetails, onCancel }) => {
    const { themeStyle } = useThemeContext();
    const [activeSection, setActiveSection] = useState<EditSection>('basic');
    const [isEditing, setIsEditing] = useState(true);
    const [hasChanges, setHasChanges] = useState(false);
    const currentSectionSaveRef = useRef<(() => void) | null>(null);

    // Check if current user is the creator of the company
    const { user_id } = getJWtDetails();
    const isCompanyCreator = user_id === companyDetails?.company?.created_by?._id;

    const handleSave = useCallback(() => {
        // Call the current section's save function
        if (currentSectionSaveRef.current) {
            currentSectionSaveRef.current();
        }
        setIsEditing(false);
        setHasChanges(false);
    }, []);

    const handleCancel = useCallback(() => {
        setIsEditing(false);
        setHasChanges(false);
        onCancel?.();
    }, [onCancel]);

    const handleSectionChanges = useCallback((hasChanges: boolean) => {
        setHasChanges(hasChanges);
    }, []);

    const handleSectionSave = useCallback((saveFunction: () => void) => {
        currentSectionSaveRef.current = saveFunction;
    }, []);

    // If user is not the creator, don't show edit functionality
    if (!isCompanyCreator) {
        return (
            <Container
                sx={{
                    display: 'flex',
                    width: '100vw',
                    height: '60vh',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                <Typography sx={{ color: theme[themeStyle].tag_white, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                    You don't have permission to edit this company
                </Typography>
            </Container>
        );
    }

    const renderSection = () => {
        switch (activeSection) {
            case 'basic':
                return (
                    <CompanyBasicInfoEdit
                        company={companyDetails?.company}
                        isEditing={isEditing}
                        onSave={handleSectionSave}
                        onChanges={handleSectionChanges}
                    />
                );
            case 'about':
                return (
                    <CompanyAboutEdit
                        company={companyDetails?.company}
                        isEditing={isEditing}
                        onSave={handleSectionSave}
                        onChanges={handleSectionChanges}
                    />
                );
            case 'services':
                return (
                    <CompanyServicesEdit
                        company={companyDetails?.company}
                        isEditing={isEditing}
                        onSave={handleSectionSave}
                        onChanges={handleSectionChanges}
                    />
                );
            case 'leadership':
                return (
                    <CompanyLeadershipEdit
                        company={companyDetails?.company}
                        isEditing={isEditing}
                        onSave={handleSectionSave}
                        onChanges={handleSectionChanges}
                        leadership={companyDetails?.leadership}
                    />
                );
            case 'contact':
                return (
                    <CompanyContactEdit
                        company={companyDetails?.company}
                        isEditing={isEditing}
                        onSave={handleSectionSave}
                        onChanges={handleSectionChanges}
                    />
                );
            case 'videos':
                return (
                    <CompanyVideosEdit
                        company={companyDetails?.company}
                        isEditing={isEditing}
                        onSave={handleSectionSave}
                        onChanges={handleSectionChanges}
                    />
                );
            case 'gallery':
                return (
                    <CompanyGalleryEdit
                        company={companyDetails?.company}
                        isEditing={isEditing}
                        onSave={handleSectionSave}
                        onChanges={handleSectionChanges}
                    />
                );
            default:
                return null;
        }
    };

    const sections = [
        { key: 'basic', label: 'Basic Information' },
        { key: 'about', label: 'About & Overview' },
        { key: 'services', label: 'Services & Industry' },
        { key: 'leadership', label: 'Leadership Team' },
        { key: 'contact', label: 'Contact Information' },
        { key: 'videos', label: 'Company Videos' },
        { key: 'gallery', label: 'Photo Gallery' },
    ];

    return (
        <Container
            disableGutters
            sx={{
                width: '100%',
                marginLeft: { xs: '0px', sm: '65px' },
                paddingLeft: { xs: '16px', sm: '40px' },
                paddingRight: { xs: '16px', sm: '40px' },
                paddingTop: { xs: '20px', sm: '40px' },
                overflowX: 'hidden',
                minHeight: '70vh',
                backgroundColor: theme[themeStyle].pure_black,
            }}
            maxWidth={false}>
            <Box sx={{ width: '100%', maxWidth: '1200px' }}>
                {/* Header */}
                <Box
                    sx={{
                        backgroundColor: theme[themeStyle].pure_black,
                        borderBottom: `0.5px solid ${theme[themeStyle].filters_white}`,
                        py: 2,
                        mb: 3,
                    }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 2, sm: 0 },
                        }}>
                        <Typography
                            variant="h4"
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.BOLD_HEADER,
                            }}>
                            Edit Company Profile
                        </Typography>
                        <Box display="flex" gap={2}>
                            {!isEditing ? (
                                <>
                                    <Button
                                        variant="outlined"
                                        startIcon={<X size={16} />}
                                        onClick={handleCancel}
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            borderColor: theme[themeStyle].tag_white,
                                            '&:hover': {
                                                borderColor: theme[themeStyle].primary_red,
                                                color: theme[themeStyle].primary_red,
                                            },
                                        }}>
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<Edit size={16} />}
                                        onClick={() => setIsEditing(true)}
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            borderColor: theme[themeStyle].tag_white,
                                            '&:hover': {
                                                borderColor: theme[themeStyle].highlight,
                                                color: theme[themeStyle].highlight,
                                            },
                                        }}>
                                        Edit
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button
                                        variant="outlined"
                                        startIcon={<X size={16} />}
                                        onClick={handleCancel}
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            borderColor: theme[themeStyle].tag_white,
                                            '&:hover': {
                                                borderColor: theme[themeStyle].primary_red,
                                                color: theme[themeStyle].primary_red,
                                            },
                                        }}>
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        startIcon={<Save size={16} />}
                                        onClick={handleSave}
                                        disabled={!hasChanges}
                                        sx={{
                                            backgroundColor: hasChanges
                                                ? theme[themeStyle].highlight
                                                : theme[themeStyle].darkGray60,
                                            color: theme[themeStyle].black,
                                            '&:hover': {
                                                backgroundColor: hasChanges
                                                    ? theme[themeStyle].primary_orange
                                                    : theme[themeStyle].darkGray60,
                                            },
                                        }}>
                                        Save Changes
                                    </Button>
                                </>
                            )}
                        </Box>
                    </Box>
                </Box>

                <Grid container spacing={3}>
                    {/* Navigation Sidebar */}
                    <Grid item xs={12} md={3}>
                        <Card
                            sx={{
                                bgcolor: theme[themeStyle].secondaryBg,
                                border: `1px solid ${theme[themeStyle].tag_white_light}`,
                            }}>
                            <CardHeader
                                title="Edit Sections"
                                sx={{ color: theme[themeStyle].tag_white }}
                                titleTypographyProps={{
                                    sx: { fontFamily: FONT_ENUM.MAIN_BOLD },
                                }}
                            />
                            <CardContent sx={{ p: 0 }}>
                                {sections.map(section => (
                                    <Box
                                        key={section.key}
                                        onClick={() => setActiveSection(section.key as EditSection)}
                                        sx={{
                                            p: 2,
                                            cursor: 'pointer',
                                            borderBottom: `1px solid ${theme[themeStyle].tag_white_light}`,
                                            backgroundColor:
                                                activeSection === section.key
                                                    ? theme[themeStyle].highlight
                                                    : 'transparent',
                                            '&:hover': {
                                                backgroundColor:
                                                    activeSection === section.key
                                                        ? theme[themeStyle].highlight
                                                        : theme[themeStyle].form_divider,
                                            },
                                        }}>
                                        <Typography
                                            sx={{
                                                color:
                                                    activeSection === section.key
                                                        ? theme[themeStyle].black
                                                        : theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: '0.875rem',
                                            }}>
                                            {section.label}
                                        </Typography>
                                    </Box>
                                ))}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Main Content */}
                    <Grid item xs={12} md={9}>
                        {renderSection()}
                    </Grid>
                </Grid>
            </Box>
        </Container>
    );
};

export { CompanyEditContainer };
